import * as moment from 'moment-timezone';
import {useEffect, useState} from "react";
import SplitPane, {Pane} from 'split-pane-react';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'split-pane-react/esm/themes/default.css';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import {grey} from "@mui/material/colors";
import {useLazyQuery} from "@apollo/client";

import useWindowDimensions from '../hooks/useWindowDimensions';
import useZendesk from '../hooks/useZendesk';
import {useTimezone} from '../hooks/useTimezone';
import useEntraAuthentication from '../hooks/useEntraAuthentication';
import {RefreshProvider} from '../contexts/RefreshContext';

import {MY_SHIFT_AND_BOOKINGS} from "../queries/GraphQLQueries";
import {SESSION_TYPE} from "../domain/SessionType";
import CustomAlert from "../components/CustomAlert";
import ShiftCalendar from '../components/sessions/ShiftCalendar';
import TimeslotPanel from "../components/sessions/TimeslotPanel";
import MySessionViewToggle from '../components/sessions/MySessionViewToggle';
import PanelSwapToggle from '../components/sessions/PanelSwapToggle';
import ContentViewPanel from '../components/sessions/ContentViewPanel';
import PopOutDialog from '../components/sessions/PopOutDialog';
import {ApolloFetchPolicies} from '../constants/FetchPolicies';
import {
    buildBookedSessionEvents,
    buildNonTeachingSessionEvents,
    buildShiftEvents,
    getEndTime,
    VIEW_TYPES,
    getContentHeight
} from "../helpers/SessionHelper";
import {toTitleCase} from "../helpers/TextUtils";
import {loadLayoutPreferences, saveLayoutPreferences} from "../helpers/MySessionsStorageHelper";

const splitDivider = () => (
  <Box sx={{
    width: '100%',
    height: '100%',
    backgroundColor: grey[500],
    cursor: 'col-resize'
  }}/>
);

const headerBoxStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingRight: '1rem'
};

export const BREAK_TIMESLOT_TITLE = "Scheduled Break";
export const TRAINING_TIMESLOT_TITLE = "Scheduled Training";

export default function MySessionsView() {

    const {windowHeight} = useWindowDimensions();
    const splitPaneHeight = {
        height: getContentHeight(windowHeight)
    };

    const {timezone} = useTimezone();
    const CALENDAR_STEP_SIZE_IN_MINUTES = 15;
    const SHIFT_POLLING_RATE = 50000;
    const NUMBER_OF_EXTRA_DAYS_FOR_FETCHING_SESSIONS_DATA = 1;

    const savedLayout = loadLayoutPreferences();

    const [selectedTimeslot, setSelectedTimeslot] = useState();
    const [selectedSessionServiceTypeDetails, setSelectedSessionServiceTypeDetails] = useState();
    const [timeslots, setTimeslots] = useState();
    const [timeslotEvents, setTimeslotEvents] = useState();
    const [errorMessage, setErrorMessage] = useState(null);
    const [shiftEvents, setShiftEvents] = useState();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [verticalSplitPercentage, setVerticalSplitPercentage] = useState(savedLayout.verticalSplit);
    const [horizontalSplitPercentage, setHorizontalSplitPercentage] = useState(savedLayout.horizontalSplit);
    const [currentView, setCurrentView] = useState(savedLayout.viewType);
    const [clickedPdfUrl, setClickedPdfUrl] = useState(null);
    const [clickedPdfName, setClickedPdfName] = useState(null);
    const [popOutPdfUrl, setPopOutPdfUrl] = useState(null);
    const [popOutPdfName, setPopOutPdfName] = useState(null);
    const [isResizing, setIsResizing] = useState(false);
    const [panelOrder, setPanelOrder] = useState(savedLayout.panelOrder);
    const [isPopOutOpen, setIsPopOutOpen] = useState(false);
    const [dialogSize, setDialogSize] = useState({width: '80vw', height: '80vh'});
    const [lastRefreshTime, setLastRefreshTime] = useState(moment.tz(timezone));

    useZendesk();

    const entraAuthentication = useEntraAuthentication();

    const [getShiftAndTimeslots, {loading, error, data: shiftAndBookingsData}] = useLazyQuery(MY_SHIFT_AND_BOOKINGS, {pollInterval: SHIFT_POLLING_RATE, fetchPolicy: ApolloFetchPolicies.NETWORK_ONLY});

    useEffect(() => {
        saveLayoutPreferences({
            viewType: currentView,
            verticalSplit: verticalSplitPercentage,
            horizontalSplit: horizontalSplitPercentage,
            panelOrder: panelOrder
        });
    }, [currentView, verticalSplitPercentage, horizontalSplitPercentage, panelOrder]);

    useEffect(() => {
        let startTime = moment().tz(timezone).startOf('day').toISOString();
        let endTime = moment(selectedDate).tz(timezone).endOf('day').toISOString();

        if (moment(selectedDate).isSameOrBefore(moment(), 'day')) {
            startTime = moment(selectedDate).tz(timezone).startOf('day').toISOString();
            endTime = moment().tz(timezone).add(NUMBER_OF_EXTRA_DAYS_FOR_FETCHING_SESSIONS_DATA, 'day').endOf('day').toISOString()
        }

        getShiftAndTimeslots({
            variables: {
                startTime,
                endTime
            }
        });
    }, [selectedDate]);

    useEffect(() => {
        if (error) {
            console.log("Error during loading: " + error);
            setErrorMessage({children: error.message, severity: 'error'});

            setTimeout(() => {
                getShiftAndTimeslots();
            }, SHIFT_POLLING_RATE);
        }

        if (!shiftAndBookingsData || !shiftAndBookingsData.me) {
            return;
        }

        setLastRefreshTime(moment.tz(timezone));

        let mappedShiftEvents = buildShiftEvents(shiftAndBookingsData.me.availability, timezone);
        setShiftEvents(mappedShiftEvents);

        const mappedBookedSessions = buildBookedSessionEvents(shiftAndBookingsData.me.availability);
        const mappedBreakSessions = buildNonTeachingSessionEvents(shiftAndBookingsData.me.availability);

        const allSessions = [...mappedBookedSessions, ...mappedBreakSessions];
        setTimeslots(allSessions);

        let convertedSessions = allSessions.map(session => {
            
            const keycodeFromSession = session.serviceType?.keycode;
            const matchingServiceTypeFromShift = shiftAndBookingsData.serviceTypes
                .filter(serviceType => serviceType.keycode === keycodeFromSession)[0];
            
            const {
                primaryColor,
                description: serviceTypeDescription,
                keycode: serviceTypeKeyCode,
                enableLaunchSessionBeforeMinutes,
                disableLaunchSessionAfterMinutes
            } = matchingServiceTypeFromShift || {};

            const contents = session?.contents;
            const contentType = contents ? contents.map(content => content.contentType).join(', ') : '';
            const isAssessed = contents ? contents.some(content => content.isAssessed) : false;

            return {
                id: session.id,
                shiftId: session.shiftId,
                start: moment.tz(session.from, timezone).toDate(),
                end: moment.tz(getEndTime(session.to), timezone).toDate(),
                title: populateTitle(session),
                isSession: true,
                contentType: contentType,
                isAssessed: isAssessed,
                sessionType: session.sessionType,
                serviceTypeDescription,
                serviceTypeKeyCode,
                primaryColor,
                enableLaunchSessionBeforeMinutes,
                disableLaunchSessionAfterMinutes,
            }
        });

        setTimeslotEvents(convertedSessions);

    }, [shiftAndBookingsData, timezone]);

    const getContentNamesForSession = (session) => session.contents?.map(content => content.name).join(', ');

    const populateTitle = (session) => {

        const title = (session.sessionType !== SESSION_TYPE.BREAK && session.sessionType !== SESSION_TYPE.CHECK_IN)
            ? getContentNamesForSession(session)
            : toTitleCase(session.sessionType);

        return title;
    }

    const handleSelectEvent = (calendarEvent) => {
        if (!calendarEvent.isSession) {
            setSelectedTimeslot(null);
            return;
        }
        let foundTimeslot = timeslots.find(timeslot => timeslot.id === calendarEvent.id);

        if (foundTimeslot?.to) {
            setSelectedTimeslot(foundTimeslot);
        }

        setSelectedSessionServiceTypeDetails({
            "type": calendarEvent.serviceTypeKeyCode,
            "color": calendarEvent.primaryColor
        });
    }

    const handleNavigation = (date) => {
        setSelectedDate(date);
        setSelectedTimeslot(null);
    }

    const handleCloseSnackbar = () => setErrorMessage(null);

    const handleCloseSelectedTimeslot = () => setSelectedTimeslot(null);

    const handleViewChange = (newView) => {
        setCurrentView(newView);
    };

    const handlePdfClick = (pdfUrl, pdfName) => {
        setClickedPdfUrl(pdfUrl);
        setClickedPdfName(pdfName);
    };

    const handleIconSwap = () => {
        setPanelOrder(prev => prev[0] === 'sessionCard' ? ['content', 'sessionCard'] : ['sessionCard', 'content']);
    };

    const handleOpenPopOut = (pdfUrl, pdfName) => {
        setPopOutPdfUrl(pdfUrl);
        setPopOutPdfName(pdfName);
        setDialogSize({width: '80vw', height: '80vh'});
        setIsPopOutOpen(true);
    };

    const handleClosePopOut = () => {
        setIsPopOutOpen(false);
    };

    const renderDefaultView = () => (
        <Grid container alignItems="center" sx={{display: 'flex'}}>
            <Grid size={5} className={"sessionViewGrid"} sx={{flexGrow: 1}}>
                <ShiftCalendar
                    timeslotEvents={timeslotEvents}
                    shiftEvents={shiftEvents}
                    calendarStepSize={CALENDAR_STEP_SIZE_IN_MINUTES}
                    handleNavigation={handleNavigation}
                    handleSelectEvent={handleSelectEvent}/>
            </Grid>
            <TimeslotPanel
                handleCloseSelectedTimeslot={handleCloseSelectedTimeslot}
                selectedTimeslot={selectedTimeslot}
                shiftEvents={shiftEvents}
                timeslotEvents={timeslotEvents}
                selectedSessionServiceTypeDetails={selectedSessionServiceTypeDetails}
                timeslots={timeslots}/>
        </Grid>
    );

    const handleResizeStart = () => {
        setIsResizing(true);
    };

    const handleResizeEnd = () => {
        setIsResizing(false);
    };

    const renderGridView = () => {
        const panels = {
            sessionCard: (
                <Pane minSize='20%' maxSize='80%'>
                    <Box sx={{height: '100%', overflow: 'auto', backgroundColor: '#eceff1'}}>
                        <TimeslotPanel
                            handleCloseSelectedTimeslot={handleCloseSelectedTimeslot}
                            selectedTimeslot={selectedTimeslot}
                            shiftEvents={shiftEvents}
                            timeslotEvents={timeslotEvents}
                            selectedSessionServiceTypeDetails={selectedSessionServiceTypeDetails}
                            timeslots={timeslots}
                            gridView={true}
                            useSlidingTimeslotPanel={true}
                            onPdfClick={handlePdfClick}
                            onExpandContent={handleOpenPopOut}/>
                    </Box>
                </Pane>
            ),
            content: (
                <ContentViewPanel
                    clickedPdfUrl={clickedPdfUrl}
                    clickedPdfName={clickedPdfName}
                    isResizing={isResizing}
                    entraAuthentication={entraAuthentication}/>
            )
        };

        return (
            <>
              <Box sx={splitPaneHeight}>
                  <SplitPane
                    split="vertical"
                    sizes={verticalSplitPercentage}
                    onChange={setVerticalSplitPercentage}
                    allowResize={true}
                    resizerSize={4}
                    performanceMode={false}
                    sashRender={splitDivider}
                    onDragStart={handleResizeStart}
                    onDragEnd={handleResizeEnd}>
                      <Pane minSize='20%' maxSize='60%'>
                          <Grid size={5} className={"sessionViewGrid"} sx={{flexGrow: 1}}>
                              <ShiftCalendar
                                timeslotEvents={timeslotEvents}
                                shiftEvents={shiftEvents}
                                calendarStepSize={CALENDAR_STEP_SIZE_IN_MINUTES}
                                handleNavigation={handleNavigation}
                                handleSelectEvent={handleSelectEvent}/>
                          </Grid>
                      </Pane>

                      <Box sx={{height: '100%', position: 'relative'}}>
                          <SplitPane
                            split="horizontal"
                            sizes={horizontalSplitPercentage}
                            onChange={setHorizontalSplitPercentage}
                            allowResize={true}
                            resizerSize={4}
                            sashRender={splitDivider}
                            onDragStart={handleResizeStart}
                            onDragEnd={handleResizeEnd}>
                              {panels[panelOrder[0]]}
                              {panels[panelOrder[1]]}
                          </SplitPane>
                      </Box>
                  </SplitPane>
              </Box>

              <PopOutDialog
                isOpen={isPopOutOpen}
                dialogSize={dialogSize}
                onDialogSizeChange={setDialogSize}
                onClose={handleClosePopOut}
                pdfUrl={popOutPdfUrl}
                pdfName={popOutPdfName}
                entraAuthentication={entraAuthentication}
              />
            </>
        );
    };

    return (
        <RefreshProvider trigger={lastRefreshTime}>
            <Box sx={headerBoxStyle}>
                {currentView === VIEW_TYPES.GRID && (
                    <PanelSwapToggle
                        isSwapped={panelOrder[0] === 'content'}
                        onSwapToggle={handleIconSwap}/>
                )}
                <MySessionViewToggle currentView={currentView} onViewChange={handleViewChange}/>
            </Box>
            {currentView === VIEW_TYPES.DEFAULT ? renderDefaultView() : renderGridView()}
            <CustomAlert alertMessage={errorMessage} handleCloseCustomAlert={handleCloseSnackbar}/>
        </RefreshProvider>
    );
};
